import { useRef, useState } from 'react';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { ChevronLeft, PowerIcon, WaterIcon } from '../../shared/assets/icons';
import { css, cx } from '@emotion/css';
import {
  BOTTOM_NAV_SAFE_HEIGHT,
  HEADER_HEIGHT,
} from '../../shared/styles/css-values';
import { Colors } from '../../shared/styles/colors';
import {
  doctrineStencilStyle,
  labelStyle,
  p2Style,
  tabularNumbersStyle,
} from '../../shared/styles/text';
import { Source } from '../../shared/types/server-types';
import { usePedestal } from '../../shared/services/api/pedestal';
import { SocketCard } from './source-cards/SocketCard';
import { ValveCard } from './source-cards/ValveCard';
import { SourceModal } from './source-modals/SourceModal';
import { useParams } from 'react-router-dom';
import { useCurrentUser } from '../../shared/services/api/user';
import { useNavigator } from '../../routing/navigator';
import { PedestalLoadingState } from '../../shared/components/loading-states/PedestalLoadingState';
import { Layout } from '../../shared/components/layout/Layout';
import { filterSources } from '../../shared/utils/filterSources';
import { http } from '../../shared/services/http';
import { useAsyncEffect } from 'use-async-effect';
import { Radius } from '../../shared/styles/tokens';

export const PedestalPage = (): JSX.Element => {
  let { pedestalId } = useParams<{ pedestalId: string }>();
  const navigator = useNavigator();

  if (!pedestalId) {
    throw new Error();
  }
  pedestalId = pedestalId.toLowerCase();

  const {
    data: pedestal,
    refetch: refetchPedestal,
    isLoading,
    isError,
  } = usePedestal({ identifier: pedestalId });
  const {
    data: user,
    isLoading: userIsLoading,
    isError: userIsError,
    refetch: refetchUser,
  } = useCurrentUser();

  const creatingAccountRef = useRef(false);

  useAsyncEffect(async () => {
    if (pedestal && user && !creatingAccountRef.current) {
      const account = user.accounts.find(
        (account) =>
          account.location.id === pedestal.location.id ||
          account.location.subLocations?.some(
            (sl) => sl.id === pedestal.location.id,
          ),
      );
      if (!account) {
        creatingAccountRef.current = true;
        try {
          await http.createAccount(pedestal.location.id);
          await refetchUser();
        } finally {
          creatingAccountRef.current = false;
        }
      }
    }
  }, [isLoading, userIsLoading]);

  const [activeModalSource, setActiveModalSource] = useState<Source>(
    {} as Source,
  );
  const [modalVisible, setModalIsVisible] = useState<boolean>(false);

  if (isError || userIsError)
    return <p className={errorStyle}>Pedestal could not be loaded.</p>;

  if (isLoading || userIsLoading || !pedestal || !user) {
    return <PedestalLoadingState />;
  }

  const { identifier, sockets, valves } = pedestal;
  const { availablePower, availableWater } = filterSources(valves, sockets);

  const sortedSockets = sockets.sort((a, b) => a.position - b.position);
  const sortedValves = valves.sort((a, b) => a.position - b.position);

  const handleSetIsModalVisible = async (value: boolean) => {
    setModalIsVisible(value);
    await refetchPedestal();
  };

  return (
    <Layout.Content className={pedestalPageStyle}>
      <div className={homeButtonStyle}>
        <Button
          startIcon={<ChevronLeft />}
          variant={ButtonVariantEnum.TEXT}
          size={ButtonSizeEnum.LARGE}
          text={`back`}
          onClick={navigator.toPrevious}
        />
      </div>
      <div className={pedestalInfoSectionStyle}>
        <div>
          <label className={cx(labelStyle, infoLabelStyle)}>ID</label>
          <h1 className={cx(doctrineStencilStyle, identifierStyle)}>
            {identifier.toUpperCase()}
          </h1>
        </div>
        <div id="sourceInfo">
          <figure
            title={`${availablePower} of ${sockets.length} power sockets available`}
          >
            <PowerIcon />
            <figcaption className={p2Style}>
              {availablePower}/{sockets.length}
            </figcaption>
          </figure>
          <figure
            title={`${availableWater} of ${valves.length} water valves available`}
          >
            <WaterIcon />
            <figcaption className={p2Style}>
              {availableWater}/{valves.length}
            </figcaption>
          </figure>
        </div>
      </div>
      <div className={SourceSectionStyle}>
        {sortedSockets.map((socket, index) => (
          <SocketCard
            key={index}
            socket={socket}
            setModalVisible={setModalIsVisible}
            setActiveModalSource={setActiveModalSource}
          />
        ))}
      </div>
      {sortedValves.length > 0 && sortedSockets.length > 0 && (
        <div className={sourceCardDividerStyle} aria-hidden="true"></div>
      )}
      <div className={SourceSectionStyle}>
        {sortedValves.map((valve, index) => (
          <ValveCard
            key={index}
            valve={valve}
            setModalVisible={setModalIsVisible}
            setActiveModalSource={setActiveModalSource}
          />
        ))}
      </div>
      <SourceModal
        pedestal={pedestal}
        source={activeModalSource as Source}
        user={user}
        isVisible={modalVisible}
        setIsVisible={handleSetIsModalVisible}
      />
    </Layout.Content>
  );
};

const pedestalPageStyle = css`
  min-height: calc(100vh - ${HEADER_HEIGHT});
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.625rem 1.5rem calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem) 1.5rem;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const homeButtonStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 0px;
  justify-self: flex-start;
  align-self: flex-start;
  margin-bottom: 1rem;
`;

const pedestalInfoSectionStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1.5rem;

  div#sourceInfo {
    display: flex;
    gap: 0.5rem;
    figure {
      ${tabularNumbersStyle}
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem 0.375rem 0.5rem;
      background: ${Colors.SURFACE};
      border: 1px solid ${Colors.BORDER};
      border-radius: ${Radius.pill};
      svg {
        width: 1.25rem;
        height: 1.25rem;
      }
      figcaption {
        text-align: center;
        color: ${Colors.INK};
      }
    }
  }
`;

const identifierStyle = css`
  color: ${Colors.INK};
  overflow-wrap: anywhere;
`;

const infoLabelStyle = css`
  color: ${Colors.TEXT_SECONDARY};
  display: block;
  margin-bottom: 0.25rem;
`;

const SourceSectionStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  width: 100%;
`;

const sourceCardDividerStyle = css`
  width: 100%;
  display: grid;
  grid-template: auto;
  padding: 0.5rem 0;

  &::after {
    content: '';
    width: 0px;
    height: 2rem;
    border-left: 2px dashed ${Colors.BORDER_STRONG};
    margin: 0px auto;
  }
`;
